/**
 * SectionPanel — collapsible container card for the Property Panel design system.
 *
 * (Formerly `PanelCard`.) The primary unit of hierarchy: each logical group of
 * fields lives in its own bordered card with a consistent header (icon + title +
 * optional badge + chevron). Containers do the visual work, so titles can be
 * sentence-case and low-weight.
 *
 * Variants (unchanged):
 *   - "standard" (default) — white surface, hairline border, collapsible
 *   - "alert"              — danger surface/border, NOT collapsible (urgency
 *                            shouldn't be dismissable)
 *   - "subtle"             — borderless, muted surface, for de-emphasized
 *                            groupings
 *   - "bare"               — no card chrome at all (no surface, border, radius,
 *                            or horizontal padding). Same collapsible header +
 *                            body, but sits flush inline like a section divider.
 *
 * NEW — themeable header:
 *   - headerColor       — "darkBlue" | "black" | "gray" | "lightBlue" |
 *                         "lightGray" | any CSS color string. When set, the
 *                         HEADER band gets a solid fill and the title/icon/
 *                         chevron flip to a readable foreground (white on dark
 *                         presets, dark on the light presets).
 *                         (Ignored for the "alert" variant, which owns its look.)
 *   - headerForeground  — override the auto-picked foreground color. Useful for
 *                         custom headers where the auto choice isn't right.
 *   - iconBox           — when true, the icon sits inside a rounded tinted
 *                         square. When false (default), the icon floats bare.
 *   - headerDivider     — force (true) or remove (false) the hairline divider
 *                         under the header. Defaults per variant: every variant
 *                         except "bare" draws one when expanded. Useful to give
 *                         a "bare" section a header underline without the rest
 *                         of the card chrome.
 *
 * All previous props still behave exactly as before when none of the new props
 * are passed, so existing usages are unaffected.
 *
 * Not to be confused with the older `SidebarSection` — SectionPanel is the
 * design-system card and is meant to be reused beyond the sidebar.
 */
import { ExpandLess as ChevronIcon } from "@mui/icons-material";
import { Box, Collapse } from "@mui/material";
import { memo, useState } from "react";

import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import { PANEL_TOKENS as T } from "./panelTokens";

// Named header color presets. Each preset declares its background and,
// optionally, the foreground that reads on it. Light presets (e.g. lightBlue)
// want dark text; dark presets omit `fg` and fall back to white.
const HEADER_COLOR_PRESETS = {
  darkBlue: { bg: "#0C447C", hover: "#0A3A6A" },
  black: { bg: "#2C2C2A", hover: "#3A3A37" },
  gray: { bg: "#5F5E5A", hover: "#6E6D69" },
  lightBlue: { bg: "#E6F1FB", fg: "#0C447C", hover: "#D6E7F7" },
  lightGray: { bg: "#F1EFE8", fg: "#444441", hover: "#E5E3DB" },
};

// Resolve a headerColor prop (preset name OR raw CSS color) to { bg, fg? }.
const resolveHeaderColor = (headerColor) => {
  if (!headerColor) return null;
  return HEADER_COLOR_PRESETS[headerColor] ?? { bg: headerColor };
};

const SectionPanel = memo(
  ({
    icon,
    title,
    badge,
    defaultOpen = true,
    variant = "standard", // "standard" | "alert" | "subtle" | "bare"
    disableCollapse = false,
    headerEndContent,
    subTitle,
    children,
    sx,
    py = 2,
    bodySx,
    // ---- new theming props ----
    headerColor, // "darkBlue" | "black" | "gray" | "lightBlue" | "lightGray" | CSS color string
    headerForeground, // optional override for header text/icon color
    iconBox = false, // wrap the icon in a rounded tinted square
    headerDivider, // force a hairline divider under the header (defaults per variant)
  }) => {
    const [open, setOpen] = useState(defaultOpen);

    const isAlert = variant === "alert";
    const isSubtle = variant === "subtle";
    const isBare = variant === "bare";
    // Alert cards are never collapsible — see rule #6 in the design doc.
    const collapsible = !disableCollapse && !isAlert;
    const expanded = collapsible ? open : true;

    // A themed header only applies to non-alert cards (alert owns its own look).
    const resolvedHeader = isAlert ? null : resolveHeaderColor(headerColor);
    const hasThemedHeader = resolvedHeader != null;
    const resolvedHeaderColor = resolvedHeader?.bg ?? null;

    // Foreground for a themed header: explicit prop wins, then the preset's own
    // foreground, then white (the right default for the dark presets).
    const themedForeground =
      headerForeground ?? resolvedHeader?.fg ?? "#FFFFFF";
    // Did we land on a real color (light theme) rather than the white default?
    // If so, secondary header bits reuse it as-is; otherwise they use a
    // translucent white so they read as "dimmed" on a dark band.
    const hasExplicitForeground =
      headerForeground != null || resolvedHeader?.fg != null;
    const themedForegroundMuted = hasExplicitForeground
      ? themedForeground
      : "rgba(255,255,255,0.65)";
    // Tint behind boxed icons / badges also depends on light vs dark header.
    const themedOverlaySoft = hasExplicitForeground
      ? "rgba(0,0,0,0.06)"
      : "rgba(255,255,255,0.18)";
    const themedOverlayIcon = hasExplicitForeground
      ? "rgba(0,0,0,0.05)"
      : "rgba(255,255,255,0.14)";

    const surface = isAlert
      ? T.color.dangerBg
      : isSubtle
        ? T.color.bgSubtle
        : isBare
          ? "transparent"
          : T.color.bgCard;
    const borderColor = isAlert ? T.color.dangerBorder : T.color.borderDefault;

    // Bare variant has no card chrome, so it sits flush with surrounding
    // content — no surface, border, radius, or horizontal padding.
    const headerPx = isBare ? 0 : T.space.cardPaddingX;
    const bodyPx = isBare ? 0 : T.space.cardPaddingX;

    // Whether to draw a hairline divider under the header. `headerDivider`
    // explicitly overrides; otherwise default per variant (every variant except
    // bare draws one when expanded). A themed header suppresses the line by
    // default since the colored band already separates header from body — but
    // an explicit `headerDivider` still forces it.
    const dividerDefault = expanded && !isBare;
    const showDivider = headerDivider != null ? headerDivider : dividerDefault;
    const dividerColor =
      hasThemedHeader && headerDivider == null ? "transparent" : borderColor;

    // Title / icon colors. Themed header overrides the usual logic.
    const titleColor = hasThemedHeader
      ? themedForeground
      : isAlert
        ? T.color.dangerTextStrong
        : T.color.textPrimary;
    const iconColor = hasThemedHeader
      ? themedForeground
      : isAlert
        ? T.color.dangerText
        : T.color.textSecondary;
    const chevronColor = hasThemedHeader
      ? themedForegroundMuted
      : T.color.textSecondary;
    const subTitleColor = hasThemedHeader ? themedForegroundMuted : undefined;

    // Badge background needs to sit on whatever header surface is behind it.
    const badgeBg = hasThemedHeader
      ? themedOverlaySoft
      : isAlert
        ? "rgba(255,255,255,0.55)"
        : T.color.bgSubtle;
    const badgeColor = hasThemedHeader
      ? themedForeground
      : T.color.textTertiary;

    // The header background: themed color, else transparent (inherits card).
    const headerBg = hasThemedHeader ? resolvedHeaderColor : "transparent";
    // On a themed header, hover uses a SOLID color (not a translucent overlay).
    // A transparent overlay applied via backgroundColor would replace the solid
    // `background` shorthand set on the base — wiping the fill to near-white.
    // Presets ship their own hover shade; custom colors blend toward black/white.
    const headerHoverBg = hasThemedHeader
      ? (resolvedHeader?.hover ??
        (hasExplicitForeground
          ? `color-mix(in srgb, ${resolvedHeaderColor} 92%, #000)`
          : `color-mix(in srgb, ${resolvedHeaderColor} 88%, #fff)`))
      : T.color.bgSubtle;

    // Shared icon renderer — bare or boxed.
    const renderIcon = () => {
      if (!icon) return null;
      if (iconBox) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: T.radius.md,
              background: hasThemedHeader
                ? themedOverlayIcon
                : T.color.bgSubtle,
              color: iconColor,
              "& svg": { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        );
      }
      return (
        <Box
          sx={{
            display: "flex",
            flexShrink: 0,
            color: iconColor,
            "& svg": { fontSize: 16 },
          }}
        >
          {icon}
        </Box>
      );
    };

    const renderBadge = () =>
      badge != null && badge !== "" ? (
        <Txt
          sx={{
            flexShrink: 0,
            fontSize: T.text.xs,
            color: badgeColor,
            px: "6px",
            py: "2px",
            borderRadius: T.radius.sm,
            background: badgeBg,
          }}
        >
          {badge}
        </Txt>
      ) : null;

    const renderTitle = () => (
      <Txt
        sx={{
          fontSize: T.text.md,
          fontWeight: T.weight.medium,
          color: titleColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </Txt>
    );

    return (
      <Box
        sx={{
          background: surface,
          border: isSubtle || isBare ? "none" : `0.5px solid ${borderColor}`,
          borderRadius: isBare ? 0 : T.radius.lg,
          mb: 1.5,
          overflow: isBare ? "visible" : "hidden",
          ...sx,
        }}
      >
        <Box
          onClick={collapsible ? () => setOpen((o) => !o) : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: headerPx,
            py: T.space.cardPaddingY,
            background: headerBg,
            cursor: collapsible ? "pointer" : "default",
            userSelect: "none",
            borderBottom: showDivider
              ? `0.5px solid ${dividerColor}`
              : "0.5px solid transparent",
            transition: "background-color 0.1s",
            "&:hover": collapsible
              ? { backgroundColor: headerHoverBg }
              : undefined,
          }}
        >
          {subTitle ? (
            <Flx column g={0.5} sx={{ minWidth: 0 }}>
              <Flx ac g={1} sx={{ minWidth: 0 }}>
                {renderIcon()}
                {renderTitle()}
                {renderBadge()}
              </Flx>
              <Txt
                secondary
                sx={subTitleColor ? { color: subTitleColor } : undefined}
              >
                {subTitle}
              </Txt>
            </Flx>
          ) : (
            <Flx ac g={1} sx={{ minWidth: 0 }}>
              {renderIcon()}
              {renderTitle()}
              {renderBadge()}
            </Flx>
          )}
          <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
            {headerEndContent && (
              // Stop clicks on header-end content (buttons, inputs, etc.)
              // from bubbling up to the header's collapse toggle — interacting
              // with an action there shouldn't expand/collapse the card.
              <Box
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                {headerEndContent}
              </Box>
            )}
            {collapsible && (
              <ChevronIcon
                sx={{
                  fontSize: 14,
                  color: chevronColor,
                  transition: "transform 150ms ease",
                  transform: open ? "rotate(0deg)" : "rotate(180deg)",
                }}
              />
            )}
          </Flx>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ px: bodyPx, py, ...bodySx }}>{children}</Box>
        </Collapse>
      </Box>
    );
  },
);

SectionPanel.displayName = "SectionPanel";

export default SectionPanel;
